/**
 * Maps a PortalEmailItem from the API to the shape expected by
 * EmailViewModal / EmailListItem / EmailDetailPanel.
 *
 * Extracted from PortalInboxPage so PortalLayout can reuse it
 * when opening the email modal from action items.
 */
import type { PortalEmailItem } from '../types/portal';
import { formatTimeAgo } from './dateFormatters';

export function mapEmailToComponent(email: PortalEmailItem) {
  return {
    id: email.id,
    from: email.from_name,
    email: email.email,
    toAddress: email.to_address || undefined,
    ccAddresses: email.cc_addresses || undefined,
    subject: email.subject,
    preview: email.preview,
    body: email.body || email.preview,
    bodyHtml: email.body_html || null,
    time: email.received_at ? formatTimeAgo(email.received_at) : email.time,
    receivedAt: email.received_at || undefined,
    syncedAt: email.synced_at || undefined,
    lateSyncDays: email.late_sync_days ?? undefined,
    tags: email.tags,
    isUnread: email.is_unread,
    isFlagged: email.is_flagged,
    isNewCustomer: email.is_new_customer,
    isInternal: email.is_internal,
    isAssigned: email.is_assigned,
    assignedTo: email.assigned_to || undefined,
    status: email.status,
    hasOrder: email.has_order,
    orderRecordStatus: email.order_record_status ?? undefined,
    dupPoWarning: email.dup_po_warning ?? undefined,
    dupPoMatches: email.dup_po_matches ?? [],
    noDraftReason: email.no_draft_reason ?? undefined,
    editProposal: email.edit_proposal ?? null,
    orderDetails: email.order_details
      ? {
          poNumber: email.order_details.order_no || undefined,
          products: email.order_details.products || undefined,
          estValue: email.order_details.est_value || undefined,
          weight: email.order_details.weight || undefined,
          confidence: email.order_details.confidence ?? undefined,
        }
      : undefined,
    classificationCategory: email.classification_category ?? undefined,
    classificationConfidence: email.classification_confidence ?? undefined,
    confidenceBucket: email.confidence_bucket ?? null,
    aiDraftState: email.ai_draft_state ?? null,
    extractionConcerns: email.extraction_concerns ?? [],
    proposals: email.proposals ?? null,
    reasoning: email.reasoning ?? undefined,
    notificationDecision: email.notification_decision ?? undefined,
    decisionNotificationId: email.decision_notification_id ?? undefined,
    decisionFeedbackProcessed: email.decision_feedback_processed ?? false,
    atAGlance: email.at_a_glance ?? undefined,
    recommendedAction: email.recommended_action ?? undefined,
    recommendedActionReasoning: email.recommended_action_reasoning ?? undefined,
    notificationFeedback: email.notification_feedback ?? undefined,
    notificationFeedbackWorth: email.notification_feedback_worth ?? undefined,
    notificationFeedbackActionUseful: email.notification_feedback_action_useful ?? undefined,
    shipping: email.shipping
      ? {
          address: email.shipping.address || '',
          state: email.shipping.state || '',
        }
      : undefined,
    customer: email.customer
      ? {
          name: email.customer.name || email.from_name,
          type: email.customer.type,
          customerNo: email.customer.customer_no || undefined,
          slug: email.customer.slug || undefined,
          account: email.customer.account || undefined,
          lastOrder: email.customer.last_order || undefined,
          lastOrderValue: email.customer.last_order_value || undefined,
          domain: (email.customer.email?.split('@')[1]) || email.email.split('@')[1] || undefined,
          isManualOverride: email.customer.is_manual_override || false,
        }
      : {
          name: email.from_name,
          type: email.is_new_customer ? 'New Customer' : 'Unknown',
          domain: email.email.split('@')[1] || undefined,
          isManualOverride: false,
        },
    // Action item dismiss/resolve state
    notificationIsDismissed: email.notification_is_dismissed ?? false,
    notificationDismissedAt: email.notification_dismissed_at ?? undefined,
    // Priority fields
    priorityScore: email.priority_score ?? undefined,
    daysStale: email.days_stale ?? undefined,
    needsAttention: email.needs_attention,
    attentionReasons: email.attention_reasons,
    // Chain fields
    threadId: email.thread_id ?? undefined,
    emailCount: email.email_count ?? 1,
    // Attachments
    attachments: email.attachments ?? [],
  };
}
